import { chain } from '@leprecoins/effector';
import { attach } from 'effector';
import { AccountReportRegistryModel } from 'entities/account';
import {
	$t,
	createExportReportModel,
	EXPORT_FORMAT,
	ExportFormat,
	ExportReportModel,
} from 'shared/lib';

export type ExportReportAccountsModel = ExportReportModel;

export const createExportReportAccountsModel = (
	model: AccountReportRegistryModel,
): ExportReportModel => {
	const exportReportModel = createExportReportModel();

	const exportFx = attach({
		source: [
			model.model.$rows,
			model.$total,
			model.$isMultiCurrencies,
			$t,
			model.dateFilterModel.dateFilter.$value,
		] as const,
		effect: ([rows, total, isMultiCurrencies, t, dateFilter], format: ExportFormat) => {
			const headers = [
				t('account-report-registry.table-columns.account'),
				t('account-report-registry.table-columns.currency'),
				t('account-report-registry.table-columns.deposits'),
				t('account-report-registry.table-columns.withdrawals'),
				t('account-report-registry.table-columns.income'),
				t('account-report-registry.table-columns.rtp'),
				t('account-report-registry.table-columns.number-of-bets'),
			];

			const body = rows.map((row) => [
				typeof row.account === 'string' ? row.account : (row.account?.name ?? ''),
				row.currency,
				row.deposits,
				row.withdrawals,
				row.income,
				`${(row.rtp * 100).toFixed(2)} %`,
				row.numberOfBets,
			]);

			const footer = [
				t('account-report-registry.table-footer.total'),
				total?.currency || '',
				isMultiCurrencies ? '' : total?.deposits || 0,
				isMultiCurrencies ? '' : total?.withdrawals || 0,
				isMultiCurrencies ? '' : total?.income || 0,
				`${total?.rtp ?? 0} %`,
				total?.numberOfBets || 0,
			];

			if (format === EXPORT_FORMAT.pdf) {
				exportReportModel.saveAsPdfFx({
					head: [headers],
					body: [...body],
					footer: [footer],
					dates: dateFilter,
					filenameTemplate: t('accounts-report%DATE%.pdf'),
				});

				return;
			}

			exportReportModel.saveAsExcelFx({
				data: [headers, ...body, footer],
				dates: dateFilter,
				filenameTemplate: 'accounts-report%DATE%.xlsx',
				sheetTitle: 'Accounts Report',
			});
		},
	});

	chain(exportReportModel.exportButtonClicked, exportFx);

	return exportReportModel;
};
